/**
 * TopluAktivasyonMaili.jsx — Kullanıcılar › "Aktivasyon maili gönder" (backlog #572, backend #571, GEÇİCİ çözüm).
 *
 * Listede UYGULANMIŞ süzgece (arama + Filtreler paneli) uyan kullanıcılara, her birine YENİ bir şifre belirleme bağlantısıyla aktivasyon maili.
 * Küme backend'indir: sayfa kimlik listesi göndermez, yalnızca listenin kendi süzgeç parametrelerini (süzgeçsiz istek backend'de 400).
 * Üç adım, tek pencerede:
 *   1) ÖNİZLEME (kuru çalıştırma, hiçbir şey yazılmaz): kaç kişi eşleşti, kaç kişiye gidecek, kimler neden gitmeyecek (adres yok / geçersiz, pasif,
 *      yakın zamanda gitmiş, personel, kök), tek çalıştırmanın sınırı, güvenli modun engelleyeceği postalar, "yeni bağlantı öncekini iptal eder" uyarısı.
 *   2) BAŞLAT: önizlemenin `willSendNow` sayısı `onayliSayi` olarak gider; küme o arada büyüdüyse backend 409 verir ve sayfa yeniden saymayı önerir.
 *      Büyük bir sayıda (BUYUK_ESIK'ten fazla kişi) başlatmak için sayıyı yazmak gerekir. Güvenli mod her postayı engelleyecekse başlatılamaz.
 *   3) İLERLEME: iş numarası adreste (`?aktivasyonIs=`) durur, sayfa yenilense de aynı işe bağlanır; sayaçlar, ilk hatalar, İptal. İş sunucuda
 *      çalışmaya devam eder: pencere kapatılınca iş durmaz (yeniden açınca önizleme "çalışan iş var" der ve izlemeyi önerir).
 *
 * Yalnızca MANAGE_USER_ROLES sahibine çizilir (sayfa belirler; backend de denetler).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Badge, Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Progress, Spinner, Table } from "reactstrap";
import { useTranslation } from "react-i18next";
import {
  baslatTopluAktivasyon, getTopluAktivasyonDurumu, getTopluAktivasyonOnizleme, iptalTopluAktivasyon,
} from "../../../services/userService";

/** Bu sayıdan fazla kişiye gidecekse başlatmak için sayıyı yazmak gerekir (backlog #572: "örn. 20'nin üstü"). */
export const BUYUK_ESIK = 20;
const POLL_MS = 2000;

const DURUM_RENGI = { RUNNING: "primary", DONE: "success", CANCELLED: "secondary", STOPPED: "danger" };

export default function TopluAktivasyonMaili({ parametreler, isId, onIsId, onKapat }) {
  const { t } = useTranslation("yonetim");
  const [zorla, setZorla] = useState(false);
  const [sayac, setSayac] = useState(0);              // "yeniden say" / "yenile"
  const [onizleme, setOnizleme] = useState(null);
  const [onizlemeYukleniyor, setOnizlemeYukleniyor] = useState(!isId);
  const [hata, setHata] = useState(null);
  const [onay, setOnay] = useState("");
  const [baslatiyor, setBaslatiyor] = useState(false);
  const [isDurumu, setIsDurumu] = useState(null);     // işin ilerlemesi
  const [isHatasi, setIsHatasi] = useState(null);
  const [iptalIstendi, setIptalIstendi] = useState(false);

  // 1) önizleme — iş izlenirken yapılmaz. KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (isId) return undefined;
    let ignore = false;
    async function run() {
      setOnizlemeYukleniyor(true);
      setHata(null);
      try {
        const d = await getTopluAktivasyonOnizleme(parametreler, zorla);
        if (!ignore) setOnizleme(d);
      } catch (e) {
        if (!ignore) { setOnizleme(null); setHata(e.message); }
      } finally {
        if (!ignore) setOnizlemeYukleniyor(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [parametreler, zorla, isId, sayac]);

  // 3) ilerleme — iş çalıştığı sürece 2 sn'de bir; bitince durur.
  useEffect(() => {
    if (!isId) return undefined;
    let ignore = false;
    let zamanlayici = null;
    async function tick() {
      try {
        const d = await getTopluAktivasyonDurumu(isId);
        if (ignore) return;
        setIsDurumu(d);
        setIsHatasi(null);
        if (d.state === "RUNNING") zamanlayici = setTimeout(tick, POLL_MS);
      } catch (e) {
        if (!ignore) setIsHatasi(e);
      }
    }
    tick();
    return () => { ignore = true; if (zamanlayici) clearTimeout(zamanlayici); };
  }, [isId, sayac]);

  const gonderilecek = onizleme?.willSendNow ?? 0;
  const hepsiEngelli = Boolean(onizleme) && gonderilecek > 0 && onizleme.blockedBySafeMode >= gonderilecek;
  const onayGerekli = gonderilecek > BUYUK_ESIK;
  const onayTamam = !onayGerekli || onay.trim() === String(gonderilecek);
  const baslatilabilir = Boolean(onizleme) && !onizlemeYukleniyor && !baslatiyor && gonderilecek > 0 && !hepsiEngelli
    && !onizleme.runningJobId && onayTamam;

  async function baslat() {
    setBaslatiyor(true);
    setHata(null);
    try {
      const d = await baslatTopluAktivasyon(parametreler, zorla, onizleme.willSendNow);
      setIsDurumu(d);
      setIptalIstendi(false);
      onIsId(d.jobId);
    } catch (e) {
      setHata(e.message);   // 409: iş çalışıyor / küme büyüdü / kimse yok / hepsi engelli, 400: süzgeç yok ya da çok geniş — backend'in kendi metni
    } finally {
      setBaslatiyor(false);
    }
  }

  async function iptalEt() {
    try {
      setIsDurumu(await iptalTopluAktivasyon(isId));
      setIptalIstendi(true);
    } catch (e) {
      setIsHatasi(e);
    }
  }

  const calisiyor = isDurumu?.state === "RUNNING";
  // Pencere kapanınca iş durmaz; liste yalnızca bir iş başlatıldı / izlendiyse yenilenir (kartlardaki "aktivasyon maili" tarihi değişmiş olabilir).
  function kapat() {
    onIsId("");
    onKapat(Boolean(isDurumu));
  }

  const satir = (etiket, deger, ekstra = {}) => (
    <tr className={ekstra.vurgu ? "table-light fw-semibold" : undefined}>
      <td>{etiket}</td>
      <td className="text-end">{deger}</td>
    </tr>
  );

  function onizlemeGorunumu() {
    return (
      <>
        <p className="small text-muted">{t("topluAktivasyon.aciklama")}</p>
        {onizlemeYukleniyor && !onizleme && <div className="py-3"><Spinner size="sm" className="me-2" />{t("topluAktivasyon.sayiliyor")}</div>}
        {hata && (
          <Alert fade={false} color="danger" className="small">
            {hata}
            <div className="mt-2"><Button size="sm" color="light" className="border" onClick={() => setSayac((n) => n + 1)}>{t("topluAktivasyon.yenidenSay")}</Button></div>
          </Alert>
        )}
        {onizleme && (
          <>
            <Table size="sm" responsive className="mb-3" style={onizlemeYukleniyor ? { opacity: 0.5 } : undefined}>
              <tbody>
                {satir(t("topluAktivasyon.eslesen"), onizleme.matched)}
                {satir(t("topluAktivasyon.gonderilecek"), onizleme.willSend)}
                {satir(t("topluAktivasyon.buKosu"), onizleme.willSendNow, { vurgu: true })}
              </tbody>
            </Table>
            {onizleme.capped && <Alert fade={false} color="info" className="small">{t("topluAktivasyon.sinir", { sinir: onizleme.cap })}</Alert>}

            {(onizleme.noEmail + onizleme.badEmail + onizleme.inactive + onizleme.sentRecently + onizleme.staff + onizleme.root) > 0 && (
              <>
                <h6 className="small text-uppercase text-muted">{t("topluAktivasyon.gitmeyecekler")}</h6>
                <Table size="sm" responsive className="mb-3">
                  <tbody>
                    {onizleme.noEmail > 0 && satir(t("topluAktivasyon.epostaYok"), onizleme.noEmail)}
                    {onizleme.badEmail > 0 && satir(t("topluAktivasyon.epostaBozuk"), onizleme.badEmail)}
                    {onizleme.inactive > 0 && satir(t("topluAktivasyon.pasif"), onizleme.inactive)}
                    {onizleme.sentRecently > 0 && satir(t("topluAktivasyon.yakinda", { saat: onizleme.recentHours }), onizleme.sentRecently)}
                    {onizleme.staff > 0 && satir(t("topluAktivasyon.personel"), onizleme.staff)}
                    {onizleme.root > 0 && satir(t("topluAktivasyon.kok"), onizleme.root)}
                  </tbody>
                </Table>
              </>
            )}
            {onizleme.sharedAddress > 0 && (
              <p className="small text-muted">{t("topluAktivasyon.ortakAdres", { adet: onizleme.sharedAddress })}</p>
            )}

            {onizleme.blockedBySafeMode > 0 && (
              <Alert fade={false} color="warning" className="small">
                {hepsiEngelli
                  ? t("topluAktivasyon.hepsiEngelli")
                  : t("topluAktivasyon.guvenliMod", { adet: onizleme.blockedBySafeMode })}
                {onizleme.blockedReason && <div className="mt-1">{t("topluAktivasyon.neden")}: {onizleme.blockedReason}</div>}
              </Alert>
            )}

            <div className="form-check mb-3">
              <Input id="toplu-zorla" className="form-check-input" type="checkbox" checked={zorla} onChange={(e) => setZorla(e.target.checked)} />
              <label className="form-check-label small" htmlFor="toplu-zorla">{t("topluAktivasyon.zorla", { saat: onizleme.recentHours })}</label>
            </div>

            {onizleme.warning && <Alert fade={false} color="warning" className="small">{onizleme.warning}</Alert>}

            {onizleme.runningJobId && (
              <Alert fade={false} color="info" className="small d-flex align-items-center justify-content-between flex-wrap gap-2">
                <span>{t("topluAktivasyon.calisanIs")}</span>
                <Button size="sm" color="primary" onClick={() => onIsId(onizleme.runningJobId)}>{t("topluAktivasyon.isiTakipEt")}</Button>
              </Alert>
            )}

            {gonderilecek === 0 && !onizleme.runningJobId && <Alert fade={false} color="secondary" className="small mb-0">{t("topluAktivasyon.kimseYok")}</Alert>}

            {onayGerekli && !hepsiEngelli && !onizleme.runningJobId && (
              <div className="mb-2">
                <label className="form-label small" htmlFor="toplu-onay">{t("topluAktivasyon.onayYaz", { adet: gonderilecek })}</label>
                <Input id="toplu-onay" bsSize="sm" inputMode="numeric" autoComplete="off" value={onay} onChange={(e) => setOnay(e.target.value)} style={{ maxWidth: 160 }} />
              </div>
            )}
          </>
        )}
      </>
    );
  }

  function isGorunumu() {
    if (isHatasi && !isDurumu) {
      return (
        <Alert fade={false} color="danger" className="small">
          {isHatasi.status === 404 ? t("topluAktivasyon.isBulunamadi") : isHatasi.message}
          {isHatasi.status !== 404 && (
            <div className="mt-2"><Button size="sm" color="light" className="border" onClick={() => setSayac((n) => n + 1)}>{t("topluAktivasyon.yenile")}</Button></div>
          )}
        </Alert>
      );
    }
    if (!isDurumu) return <div className="py-3"><Spinner size="sm" className="me-2" />{t("common:yukleniyor")}</div>;
    const yuzde = isDurumu.total ? Math.min(100, Math.round((isDurumu.processed * 100) / isDurumu.total)) : 0;
    const hatalar = isDurumu.firstFailures || [];
    return (
      <>
        {isHatasi && <Alert fade={false} color="warning" className="small">{isHatasi.message}</Alert>}
        <div className="d-flex align-items-center gap-2 mb-2">
          <Badge color={DURUM_RENGI[isDurumu.state] || "secondary"}>{t(`topluAktivasyon.durum.${isDurumu.state}`, { defaultValue: isDurumu.state })}</Badge>
          <span className="small">{t("topluAktivasyon.ilerleme", { islenen: isDurumu.processed, toplam: isDurumu.total })}</span>
        </div>
        <Progress value={yuzde} animated={calisiyor} striped={calisiyor} color={DURUM_RENGI[isDurumu.state] || "secondary"} className="mb-3" />
        <Table size="sm" responsive className="mb-3">
          <tbody>
            {satir(t("topluAktivasyon.gonderildi"), isDurumu.sent)}
            {satir(t("topluAktivasyon.basarisiz"), isDurumu.failed)}
            {satir(t("topluAktivasyon.atlandi"), isDurumu.skipped)}
            {satir(t("topluAktivasyon.engellendi"), isDurumu.blocked)}
          </tbody>
        </Table>
        {isDurumu.state === "STOPPED" && (
          <Alert fade={false} color="danger" className="small">
            {t("topluAktivasyon.durdu")}{isDurumu.stoppedReason ? `: ${isDurumu.stoppedReason}` : ""}
          </Alert>
        )}
        {calisiyor && iptalIstendi && <Alert fade={false} color="secondary" className="small">{t("topluAktivasyon.iptalIstendi")}</Alert>}
        {hatalar.length > 0 && (
          <>
            <h6 className="small text-uppercase text-muted">{t("topluAktivasyon.ilkHatalar")}</h6>
            <Table size="sm" responsive className="small mb-0">
              <thead><tr><th>{t("topluAktivasyon.kullanici")}</th><th>{t("topluAktivasyon.neden")}</th></tr></thead>
              <tbody>
                {hatalar.map((h) => (
                  <tr key={h.userId}>
                    <td><Link to={`/Users/${h.userId}`} target="_blank" rel="noreferrer">#{h.userId}</Link></td>
                    <td className="text-break">{h.reason}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </>
    );
  }

  return (
    <Modal isOpen toggle={kapat} size="lg" scrollable>
      <ModalHeader toggle={kapat}>{t("topluAktivasyon.baslik")}</ModalHeader>
      <ModalBody>{isId ? isGorunumu() : onizlemeGorunumu()}</ModalBody>
      <ModalFooter>
        {isId ? (
          <>
            {calisiyor && !iptalIstendi && <Button color="danger" outline onClick={iptalEt}>{t("topluAktivasyon.iptalEt")}</Button>}
            <Button color="light" onClick={kapat}>{t("common:kapat")}</Button>
          </>
        ) : (
          <>
            <Button color="light" onClick={kapat} disabled={baslatiyor}>{t("common:iptal")}</Button>
            <Button color="primary" disabled={!baslatilabilir} onClick={baslat}>
              {baslatiyor ? <Spinner size="sm" /> : t("topluAktivasyon.baslat", { adet: gonderilecek })}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}
